import type { RefObject } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useSDK, useUIState } from '../../../../../app/App';
import { checkFileType } from '../../../utils/checkFileType';
import { getFileType } from '../../../utils/getFileType';
import { msgs } from './msgs';

const MAX_ATTACHMENTS = 2;

export type onAttachmentLoad = {
	onAttachmentLoad: VoidFunction;
};

export const useOnAttachmentLoad = (attachmentsInputRef: RefObject<HTMLInputElement>): onAttachmentLoad => {
	const { attachmentErrorsStore } = useUIState();
	const { attachmentsStore, welcomeDataStore, conversationsStore } = useSDK();
	const { formatMessage } = useIntl();
	const cb = useCallback(async (): Promise<void> => {
		if (attachmentsInputRef.current) {
			const files = attachmentsInputRef.current?.files;
			if (files?.length) {
				attachmentErrorsStore.clearError();
				if (conversationsStore.isLatestConversationResolved && attachmentsStore.unsentAttachments.size > 0) {
					attachmentErrorsStore.setError(formatMessage(msgs.maxOneFile));
					attachmentsInputRef.current.value = '';
					return;
				}
				if (attachmentsStore.unsentAttachments.size >= MAX_ATTACHMENTS) {
					attachmentErrorsStore.setError(formatMessage(msgs.maxTwoFiles));
					attachmentsInputRef.current.value = '';
					return;
				}
				const file = files[files.length - 1];
				const fileType = getFileType(file);
				if (
					welcomeDataStore.data &&
					(!fileType ||
						(fileType &&
							welcomeDataStore.data.allowedExtensions &&
							!checkFileType(welcomeDataStore.data.allowedExtensions, fileType)))
				) {
					attachmentErrorsStore.setError(
						formatMessage(msgs.fileExtensionNotAllowed, {
							allowedExtensions: welcomeDataStore.data.allowedExtensions.join(', '),
						})
					);
					attachmentsInputRef.current.value = '';
					return;
				}
				if (welcomeDataStore.data?.maxFileSize && file.size > welcomeDataStore.data.maxFileSize) {
					attachmentErrorsStore.setError(
						formatMessage(msgs.maxFileSizeLimit, {
							maxFileSize: welcomeDataStore.data.maxFileSize / (1024 * 1024),
						})
					);
					attachmentsInputRef.current.value = '';
					return;
				} else {
					await attachmentsStore.addUnsentAttachment(files[files.length - 1]);
					attachmentsInputRef.current.value = '';
					return;
				}
			}
		}
	}, [
		attachmentsStore,
		attachmentsInputRef,
		conversationsStore.isLatestConversationResolved,
		attachmentErrorsStore,
		formatMessage,
		welcomeDataStore.data,
	]);

	return { onAttachmentLoad: cb };
};
